/**
 * Поле ввода сообщения: текст, вложения и голосовые.
 *
 * C-07 — ввод, C-10 — выбор источника вложения, C-11 — предпросмотр до отправки,
 * C-12 — запись голосового, C-13 — отказ в доступе к микрофону, C-20 — ошибка
 * вложения.
 *
 * ПС §17: Enter отправляет, Shift+Enter создаёт новую строку; поведение
 * должно быть видно и ожидаемо — на desktop под полем есть подсказка (§3.4).
 * CC §2.6 п. 7: черновик переживает уход с экрана и перезапуск.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { useMediaUpload } from '@features/media/use-media-upload'
import type { LocalMediaDraft, LocalMessage } from '@shared/db'
import { draftsRepo, messagesRepo } from '@shared/db'
import { useIsDesktopLayout, useHasPointerFine } from '@shared/lib/use-media-query'
import { acceptAttribute, limitFor } from '@shared/media/limits'
import { VoiceRecorder, type RecorderState } from '@shared/media/voice-recorder'
import { syncEngine } from '@shared/sync/instance'
import { ActionItem, ActionList, Banner, Icon, Sheet, Spinner, cx, useToast } from '@shared/ui'

import { useAppStore } from '../../model/app-store'
import { formatDuration } from '../../lib/format'
import { mediaLabel } from './message-text'
import styles from './message.module.css'

/** C-16 «Режим ответа», C-17 «Режим правки» и режим пересылки (цель выбрана, ждём отправки). */
export type ComposerMode =
  | { kind: 'new' }
  | { kind: 'reply'; message: LocalMessage }
  | { kind: 'edit'; message: LocalMessage }
  | { kind: 'forward'; message: LocalMessage }

interface ComposerProps {
  conversationId: string
  mode: ComposerMode
  onModeChange: (mode: ComposerMode) => void
  onSend: (text: string, media?: LocalMediaDraft | null) => void | Promise<void>
  counterpartName: string | null
  myUserId: string
  /**
   * §4.6: `↑` в пустом поле открывает правку последнего своего сообщения,
   * если окно правки не истекло (C-17). Решение о доступности принимает
   * экран — у него есть история и конфигурация.
   */
  onEditLast?: () => boolean
  /**
   * C-25: файл, перетащенный в область чата. Зона подсветки принадлежит
   * экрану (перетаскивают на всю переписку, а не на поле ввода), а разбор
   * файла — композеру: у него и лимиты, и состояние загрузки.
   */
  droppedFile?: File | null
  onDroppedFileTaken?: () => void
}

/** Автовысота: от одной строки до 160 px, дальше — прокрутка внутри поля (§3.4). */
const MAX_HEIGHT = 144

/** `field-sizing: content` (Chromium ≥ 123) растит поле без JS; иначе — расчёт ниже. */
const NATIVE_AUTOSIZE =
  typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('field-sizing', 'content')

export function Composer({
  conversationId,
  mode,
  onModeChange,
  onSend,
  counterpartName,
  myUserId,
  onEditLast,
  droppedFile = null,
  onDroppedFileTaken,
}: ComposerProps): React.ReactElement {
  const isDesktop = useIsDesktopLayout()
  const hasPointerFine = useHasPointerFine()

  const config = useAppStore((s) => s.config)
  const accountStatus = useAppStore((s) => s.me?.user.status)

  const [text, setText] = useState('')
  const [draftLoaded, setDraftLoaded] = useState(false)
  const [sending, setSending] = useState(false)
  const toast = useToast()
  const [attachSheet, setAttachSheet] = useState<DOMRect | null>(null)

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)

  const typingStateRef = useRef<'start' | 'stop'>('stop')
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  /* --- Вложение ----------------------------------------------------------- */

  const [pickedFile, setPickedFile] = useState<File | null>(null)
  const [attachPurpose, setAttachPurpose] = useState<'chat_image' | 'chat_video'>('chat_image')
  const attachment = useMediaUpload({ config, purpose: attachPurpose, accountStatus })

  /* --- Голосовое ---------------------------------------------------------- */

  const voice = useVoiceRecording({ config, accountStatus, conversationId })

  /* --- Черновик ------------------------------------------------------------ */

  const { mediaId, previewUrl, previewBlob, contentType, width, height, durationMs, stage } = attachment.state
  const mediaDraft = useMemo<LocalMediaDraft | null>(
    () =>
      (mediaId || previewBlob) && stage !== 'rejected'
        ? {
            media_id: mediaId,
            preview_url: previewUrl,
            preview_blob: previewBlob ?? null,
            content_type: contentType ?? null,
            width: width ?? null,
            height: height ?? null,
            duration_ms: durationMs ?? null,
            ready: stage === 'ready',
          }
        : null,
    [mediaId, previewUrl, previewBlob, contentType, width, height, durationMs, stage],
  )
  const restoreRef = useRef({ mode, onModeChange, restore: attachment.restore })
  restoreRef.current = { mode, onModeChange, restore: attachment.restore }

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const draft = await draftsRepo.get(conversationId)
        const reply = draft?.reply_to_id ? await messagesRepo.byId(draft.reply_to_id) : undefined
        if (cancelled) return
        const current = restoreRef.current
        if (current.mode.kind === 'new' || current.mode.kind === 'reply') {
          setText(draft?.text ?? '')
          if (reply && current.mode.kind === 'new') current.onModeChange({ kind: 'reply', message: reply })
          if (draft?.attachments[0]) void current.restore(draft.attachments[0])
        }
      } finally {
        if (!cancelled) setDraftLoaded(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [conversationId])

  useEffect(() => {
    // До чтения IndexedDB пустой первый render не должен затирать сохранённый ответ.
    if (!draftLoaded || mode.kind === 'edit' || sending) return
    // Сохраняем сразу: переход в другой чат или refresh не ждут debounce-таймера.
    void draftsRepo.save({
      conversation_id: conversationId,
      text,
      reply_to_id: mode.kind === 'reply' ? mode.message.id : null,
      editing_message_id: null,
      attachments: mediaDraft ? [mediaDraft] : [],
    })
  }, [conversationId, draftLoaded, text, mode, mediaDraft, sending])

  const uploadAttachmentFile = attachment.uploadFile
  useEffect(() => {
    if (!pickedFile) return
    // purpose уже обновлён render-ом: видео не должно уходить как chat_image.
    void uploadAttachmentFile(pickedFile)
    setPickedFile(null)
  }, [pickedFile, uploadAttachmentFile])

  useEffect(() => {
    if (mode.kind === 'edit') {
      setText(mode.message.text ?? '')
      textareaRef.current?.focus()
    }
    if (mode.kind === 'reply' || mode.kind === 'forward') textareaRef.current?.focus()
  }, [mode])

  /* --- Автовысота ----------------------------------------------------------- */

  useLayoutEffect(() => {
    const el = textareaRef.current
    if (!el || NATIVE_AUTOSIZE) return

    /*
     * Замер через `height: auto` на миг схлопывает поле до одной строки, и
     * лента над ним успевает вырасти: браузер обрезает её scrollTop, и после
     * возврата высоты история оказывается отлистанной вверх — на каждом
     * символе. Поэтому на время замера обёртка держит прежнюю высоту.
     */
    const field = el.parentElement
    const previous = el.offsetHeight
    if (field) field.style.minHeight = `${previous}px`
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`
    // Полоса прокрутки — только когда текст действительно выше предела,
    // иначе на mobile она мелькает из-за дробных размеров.
    el.style.overflowY = el.scrollHeight > MAX_HEIGHT ? 'auto' : 'hidden'
    if (field) field.style.minHeight = ''
  }, [text])

  /* --- Индикатор набора (BS §9.3: <= 1 кадр / 3 с) -------------------------- */

  const notifyTyping = useCallback(
    (value: string) => {
      const engine = syncEngine()
      if (!engine) return

      if (value.trim() && typingStateRef.current === 'stop') {
        typingStateRef.current = 'start'
        engine.sendTyping(conversationId, 'start')
      }

      if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
      typingTimerRef.current = setTimeout(() => {
        if (typingStateRef.current === 'start') {
          typingStateRef.current = 'stop'
          engine.sendTyping(conversationId, 'stop')
        }
      }, 3000)
    },
    [conversationId],
  )

  useEffect(() => {
    return () => {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
      if (typingStateRef.current === 'start') syncEngine()?.sendTyping(conversationId, 'stop')
      typingStateRef.current = 'stop'
    }
  }, [conversationId])

  /* --- Отправка -------------------------------------------------------------- */

  const submit = useCallback(() => {
    const value = text.trim()
    if (sending || !draftLoaded || (mediaDraft && stage !== 'ready')) return
    // Пересылка уходит и без подписи: тело копирует сервер.
    if (!value && !mediaDraft && mode.kind !== 'forward') return
    setSending(true)
    void (async () => {
      try {
        await onSend(value, mediaDraft)
        setText('')
        attachment.reset()
        if (typingStateRef.current === 'start') {
          typingStateRef.current = 'stop'
          syncEngine()?.sendTyping(conversationId, 'stop')
        }
      } catch {
        toast.show('Не удалось отправить. Черновик сохранён.')
      } finally {
        setSending(false)
      }
    })()
  }, [attachment, conversationId, draftLoaded, mediaDraft, mode.kind, onSend, sending, stage, text, toast])

  /*
   * Нажатие на кнопку композера не должно уводить фокус из поля: иначе на
   * мобильных закрывается клавиатура, раскладка прыгает, и касание «Отправить»
   * промахивается мимо сдвинувшейся кнопки. Клик при этом срабатывает как обычно.
   */
  const keepFocus = (event: React.PointerEvent | React.MouseEvent): void => {
    if (document.activeElement === textareaRef.current) event.preventDefault()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      submit()
      return
    }

    /*
     * §4.6: `↑` работает только в ПУСТОМ поле и только в обычном режиме —
     * иначе это обычное перемещение курсора по тексту.
     */
    if (event.key === 'ArrowUp' && !event.shiftKey && !text && mode.kind === 'new' && onEditLast) {
      if (onEditLast()) event.preventDefault()
      return
    }

    if (event.key === 'Escape' && mode.kind !== 'new') {
      event.preventDefault()
      onModeChange({ kind: 'new' })
      if (mode.kind === 'edit') setText('')
    }
  }

  const pickFile = (file: File | undefined, purpose: 'chat_image' | 'chat_video'): void => {
    setAttachSheet(null)
    if (!file) return

    setAttachPurpose(purpose)
    setPickedFile(file)
  }

  /**
   * C-25 «Зона drag-and-drop». Тип определяется по содержимому файла, а не по
   * выбору пользователя; кнопка вложения при этом остаётся обязательной —
   * перетаскивание доступно не всем и не везде (ПС §17 «Медиа»).
   */
  const acceptDropped = (file: File | undefined): void => {
    if (!file) return
    pickFile(file, file.type.startsWith('video/') ? 'chat_video' : 'chat_image')
  }

  // C-25: файл, брошенный в область чата, проходит тот же путь, что и выбранный.
  useEffect(() => {
    if (!droppedFile) return
    acceptDropped(droppedFile)
    onDroppedFileTaken?.()
    // acceptDropped пересоздаётся каждый рендер; зависимость по файлу достаточна.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [droppedFile])

  /* --- Голосовое: отправка ---------------------------------------------------- */

  const sendVoice = async (): Promise<void> => {
    const recording = voice.recording
    if (!recording) return

    const mediaId = await voice.upload()
    if (!mediaId) return

    void onSend('', {
      media_id: mediaId,
      content_type: recording.contentType,
      preview_url: recording.previewUrl,
      duration_ms: recording.durationMs,
    })

    voice.reset()
  }

  const imageLimit = limitFor(config, 'chat_image')
  const videoLimit = limitFor(config, 'chat_video')

  /* --- Режим записи занимает весь композер (C-12) ------------------------------ */

  if (voice.state === 'recording' || voice.recording) {
    return (
      <div className={styles['composerShell']}>
        <VoicePanel
          state={voice.state}
          elapsedMs={voice.elapsedMs}
          recording={voice.recording}
          uploading={voice.uploading}
          onStop={() => void voice.stop()}
          onCancel={voice.reset}
          onSend={() => void sendVoice()}
        />
      </div>
    )
  }

  const modeTitle =
    mode.kind === 'edit'
      ? 'Редактирование'
      : mode.kind === 'forward'
        ? 'Переслать сообщение'
        : mode.kind === 'reply'
          ? `Ответ ${mode.message.sender_id === myUserId ? 'себе' : (mode.message.sender?.display_name ?? counterpartName ?? '')}`
          : ''

  const modeIcon = mode.kind === 'edit' ? 'edit' : mode.kind === 'forward' ? 'forward' : 'reply'
  const canSend = Boolean(text.trim() || attachment.state.mediaId || mode.kind === 'forward')

  return (
    <>
      {/* Единая панель ввода: подсказки, предпросмотр, режим и поле — в одном блоке с одной границей */}
      <div className={styles['composerShell']}>
        {/* C-13: отказ в доступе к микрофону */}
        {voice.state === 'denied' ? (
          <Banner tone="warning" onDismiss={voice.dismissDenied} className={styles['composerNotice']}>
            Доступ к микрофону запрещён. Разрешите его в настройках сайта — текстовые сообщения работают в
            любом случае.
          </Banner>
        ) : null}

        {/* C-20: ошибка вложения */}
        {attachment.state.error ? (
          <Banner tone="danger" onDismiss={attachment.reset} className={styles['composerNotice']}>
            {attachment.state.error}
          </Banner>
        ) : null}

        {/* C-11: предпросмотр вложения до отправки */}
        {attachment.state.stage !== 'idle' && attachment.state.stage !== 'failed' ? (
          <AttachmentPreview
            previewUrl={attachment.state.previewUrl}
            stage={attachment.state.stage}
            progress={attachment.state.progress}
            onCancel={attachment.cancel}
          />
        ) : null}

        {mode.kind !== 'new' ? (
          <div className={styles['composerBar']} key={`${mode.kind}:${mode.message.id}`}>
            <span className={styles['composerBarIcon']}>
              <Icon name={modeIcon} size={18} />
            </span>

            <div className={styles['composerBarBody']}>
              <div className={styles['composerBarTitle']}>{modeTitle}</div>
              <div className={styles['composerBarText']}>
                {mode.message.text?.trim() || mediaLabel(mode.message.type)}
              </div>
            </div>

            <button
              type="button"
              className={styles['composerButton']}
              aria-label={mode.kind === 'forward' ? 'Отменить пересылку' : 'Отменить'}
              onClick={() => {
                onModeChange({ kind: 'new' })
                if (mode.kind === 'edit') setText('')
              }}
            >
              <Icon name="close" size={18} />
            </button>
          </div>
        ) : null}

        <div className={styles['composer']}>
          <button
            type="button"
            className={styles['composerButton']}
            aria-label="Прикрепить"
            disabled={!draftLoaded || sending || mode.kind === 'edit' || mode.kind === 'forward'}
            onPointerDown={keepFocus}
            onMouseDown={keepFocus}
            onClick={(event) => setAttachSheet(event.currentTarget.getBoundingClientRect())}
          >
            <Icon name="paperclip" size={22} />
          </button>

          <div className={styles['composerField']}>
            <textarea
              ref={textareaRef}
              className={styles['composerInput']}
              value={text}
              disabled={!draftLoaded || sending}
              onChange={(event) => {
                setText(event.target.value)
                notifyTyping(event.target.value)
              }}
              onKeyDown={handleKeyDown}
              placeholder={
                mode.kind === 'edit'
                  ? 'Измените сообщение'
                  : mode.kind === 'forward'
                    ? 'Комментарий — необязательно'
                    : 'Сообщение'
              }
              rows={1}
              aria-label="Текст сообщения"
            />

            {isDesktop ? (
              <div className={styles['composerHint']}>Enter — отправить, Shift + Enter — новая строка</div>
            ) : null}
          </div>

          {canSend ? (
            <button
              type="button"
              className={cx(styles['composerButton'], styles['sendButton'])}
              onPointerDown={keepFocus}
              onMouseDown={keepFocus}
              onClick={submit}
              disabled={!draftLoaded || sending || Boolean(mediaDraft && stage !== 'ready')}
              aria-label={
                mode.kind === 'edit' ? 'Сохранить' : mode.kind === 'forward' ? 'Переслать' : 'Отправить'
              }
            >
              <Icon
                name={mode.kind === 'edit' ? 'check' : mode.kind === 'forward' ? 'forward' : 'send'}
                size={20}
              />
            </button>
          ) : (
            <button
              type="button"
              className={cx(styles['composerButton'], styles['sendButton'])}
              aria-label="Записать голосовое сообщение"
              disabled={mode.kind === 'edit'}
              onClick={() => void voice.start()}
            >
              <Icon name="mic" size={22} />
            </button>
          )}
        </div>
      </div>

      {/* C-10: выбор источника вложения */}
      <Sheet
        open={attachSheet !== null}
        onClose={() => setAttachSheet(null)}
        anchorRect={attachSheet}
        title="Добавить вложение"
        flush
      >
        <ActionList>
          <ActionItem icon="image" label="Фотография" onClick={() => imageInputRef.current?.click()} />
          <ActionItem icon="play" label="Видео" onClick={() => videoInputRef.current?.click()} />
          {!hasPointerFine ? (
            <ActionItem
              icon="camera"
              label="Снять на камеру"
              onClick={() => cameraInputRef.current?.click()}
            />
          ) : null}
        </ActionList>
      </Sheet>

      <input
        ref={imageInputRef}
        type="file"
        accept={acceptAttribute(imageLimit)}
        // §11.3 п. 4: скрытое поле остаётся в дереве доступности — ярлык обязателен.
        aria-label="Выбрать фотографию"
        className={styles['hiddenInput']}
        onChange={(event) => {
          pickFile(event.target.files?.[0], 'chat_image')
          event.target.value = ''
        }}
      />

      <input
        ref={videoInputRef}
        type="file"
        accept={acceptAttribute(videoLimit)}
        aria-label="Выбрать видео"
        className={styles['hiddenInput']}
        onChange={(event) => {
          pickFile(event.target.files?.[0], 'chat_video')
          event.target.value = ''
        }}
      />

      <input
        ref={cameraInputRef}
        type="file"
        accept={acceptAttribute(imageLimit)}
        capture="environment"
        aria-label="Снять фото на камеру"
        className={styles['hiddenInput']}
        onChange={(event) => {
          pickFile(event.target.files?.[0], 'chat_image')
          event.target.value = ''
        }}
      />
    </>
  )
}

/* ===================================================================== */
/* C-11: предпросмотр вложения                                            */
/* ===================================================================== */

function AttachmentPreview({
  previewUrl,
  stage,
  progress,
  onCancel,
}: {
  previewUrl: string | null
  stage: string
  progress: number
  onCancel: () => void
}) {
  const uploading = stage === 'uploading' || stage === 'preparing'

  return (
    <div className={styles['attachmentBar']}>
      <div className={styles['attachmentThumb']}>
        {previewUrl ? <img src={previewUrl} alt="" /> : null}
        {!uploading && stage !== 'ready' ? (
          <span className={styles['attachmentSpinner']}>
            <Spinner size={18} />
          </span>
        ) : null}
      </div>

      <div className={styles['attachmentBody']}>
        <div className={styles['attachmentTitle']}>
          {/* §8.7 п. 1: этапы передачи и обработки различаются визуально */}
          {uploading
            ? `Загрузка ${Math.round(progress * 100)}%`
            : stage === 'ready'
              ? 'Готово к отправке'
              : 'Обработка…'}
        </div>

        {uploading ? (
          <div className={styles['attachmentProgress']}>
            <div className={styles['attachmentProgressFill']} style={{ width: `${progress * 100}%` }} />
          </div>
        ) : null}
      </div>

      <button
        type="button"
        className={styles['composerButton']}
        aria-label="Убрать вложение"
        onClick={onCancel}
      >
        <Icon name="close" size={18} />
      </button>
    </div>
  )
}

/* ===================================================================== */
/* C-12: запись голосового                                                */
/* ===================================================================== */

function VoicePanel({
  state,
  elapsedMs,
  recording,
  uploading,
  onStop,
  onCancel,
  onSend,
}: {
  state: RecorderState
  elapsedMs: number
  recording: { previewUrl: string; durationMs: number } | null
  uploading: boolean
  onStop: () => void
  onCancel: () => void
  onSend: () => void
}) {
  return (
    <div className={styles['voicePanel']}>
      <button
        type="button"
        className={styles['composerButton']}
        aria-label="Отменить запись"
        onClick={onCancel}
      >
        <Icon name="trash" size={20} />
      </button>

      {state === 'recording' ? (
        <>
          <span className={styles['recordingDot']} aria-hidden />
          <span className={styles['voicePanelTime']}>{formatDuration(elapsedMs)}</span>
          <span className={styles['voicePanelHint']}>Идёт запись</span>

          <button
            type="button"
            className={cx(styles['composerButton'], styles['sendButton'])}
            aria-label="Остановить запись"
            onClick={onStop}
          >
            <Icon name="check" size={20} />
          </button>
        </>
      ) : recording ? (
        <>
          {/* До отправки доступно прослушивание и отмена (§10.4 п. 4) */}
          <VoicePreview src={recording.previewUrl} durationMs={recording.durationMs} />

          <button
            type="button"
            className={cx(styles['composerButton'], styles['sendButton'])}
            aria-label="Отправить голосовое"
            disabled={uploading}
            onClick={onSend}
          >
            {uploading ? <Spinner size={18} /> : <Icon name="send" size={20} />}
          </button>
        </>
      ) : null}
    </div>
  )
}

/**
 * Прослушивание записи до отправки — своя кнопка вместо `<audio controls>`:
 * нативный элемент показывал для webm-записи длительность «28:35:34», а его
 * вид не совпадает ни с одной темой.
 */
function VoicePreview({ src, durationMs }: { src: string; durationMs: number }): React.ReactElement {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [playing, setPlaying] = useState(false)

  useEffect(() => {
    const audio = audioRef.current
    return () => audio?.pause()
  }, [src])

  const toggle = (): void => {
    const audio = audioRef.current
    if (!audio) return
    if (playing) {
      audio.pause()
      setPlaying(false)
      return
    }
    void audio.play().then(
      () => setPlaying(true),
      () => setPlaying(false),
    )
  }

  return (
    <div className={styles['voicePreview']}>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
      />
      <button
        type="button"
        className={styles['voicePreviewPlay']}
        aria-label={playing ? 'Пауза' : 'Прослушать запись'}
        onClick={toggle}
      >
        <Icon name={playing ? 'pause' : 'play'} size={18} />
      </button>
      <span className={styles['voicePanelHint']}>{playing ? 'Воспроизведение' : 'Прослушать запись'}</span>
      <span className={styles['voicePanelTime']}>{formatDuration(durationMs)}</span>
    </div>
  )
}

/* ===================================================================== */
/* Состояние записи                                                       */
/* ===================================================================== */

function useVoiceRecording({
  config,
  accountStatus,
  conversationId,
}: {
  config: Parameters<typeof useMediaUpload>[0]['config']
  accountStatus: Parameters<typeof useMediaUpload>[0]['accountStatus']
  conversationId: string
}) {
  const toast = useToast()
  const upload = useMediaUpload({ config, purpose: 'chat_voice', accountStatus })

  const recorderRef = useRef<VoiceRecorder | null>(null)
  const [state, setState] = useState<RecorderState>('idle')
  const [elapsedMs, setElapsedMs] = useState(0)
  const [recording, setRecording] = useState<{
    blob: Blob
    contentType: string
    durationMs: number
    previewUrl: string
  } | null>(null)

  // Смена диалога сбрасывает незаконченную запись — иначе она «переедет».
  useEffect(() => {
    return () => {
      recorderRef.current?.cancel()
      recorderRef.current = null
    }
  }, [conversationId])

  /*
   * Причину отказа показываем тостом: в панели записи (C-12) места под текст нет.
   * Читать её из состояния сразу после `uploadBlob` нельзя — обработчик видит
   * снимок состояния своей отрисовки, а не тот, что выставила загрузка.
   */
  const uploadStage = upload.state.stage
  const uploadError = upload.state.error
  useEffect(() => {
    if (uploadStage !== 'failed' && uploadStage !== 'rejected') return
    toast.show(uploadError ?? 'Не удалось отправить голосовое', { tone: 'danger' })
  }, [uploadStage, uploadError, toast])

  const maxDurationSec = limitFor(config, 'chat_voice')?.max_duration_sec ?? 120

  const start = async (): Promise<void> => {
    const recorder = new VoiceRecorder({
      onStateChange: setState,
      onTick: setElapsedMs,
      onLimitReached: () => toast.show(`Достигнут предел — ${maxDurationSec} с`),
    })

    recorderRef.current = recorder
    setElapsedMs(0)
    await recorder.start(maxDurationSec)
  }

  const stop = async (): Promise<void> => {
    const result = await recorderRef.current?.stop()
    recorderRef.current = null
    if (result) setRecording(result)
    else setState('idle')
  }

  const reset = (): void => {
    recorderRef.current?.cancel()
    recorderRef.current = null

    if (recording) URL.revokeObjectURL(recording.previewUrl)
    setRecording(null)
    setElapsedMs(0)
    setState('idle')
    upload.reset()
  }

  const doUpload = async (): Promise<string | null> => {
    if (!recording) return null

    return upload.uploadBlob({
      blob: recording.blob,
      contentType: recording.contentType,
      durationMs: recording.durationMs,
    })
  }

  return {
    state,
    elapsedMs,
    recording,
    uploading: upload.state.stage === 'uploading' || upload.state.stage === 'processing',
    start,
    stop,
    reset,
    upload: doUpload,
    dismissDenied: () => setState('idle'),
  }
}
